import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import type { BoardCard } from '@/api/boardsApi'
import type { Agent } from '@/api/directoryApi'
import { thClass } from '@/components/admin/AdminShared'
import { AssigneePicker } from '@/components/board/AssigneePicker'
import type { CardAction } from '@/components/board/BoardCardView'
import type { BoardColumn } from '@/lib/boardColumns'
import { SLA_TONE_CLASS, slaCell } from '@/lib/slaDisplay'
import { priorityDotClass, statusLabel, statusPillClass } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'

/** The board as a dense list (z8nrz7fy9c): one section per column, rows open the side panel. */
export function BoardListView({
  columns,
  label,
  now,
  selected,
  agents,
  meId,
  canAssign,
  onAction,
  onOpen,
  onSelect,
}: {
  columns: BoardColumn[]
  label: (c: BoardColumn) => string
  now: number
  selected: ReadonlySet<string>
  agents: Agent[]
  meId: string | null
  canAssign: boolean
  onAction: (card: BoardCard, action: CardAction) => void
  onOpen: (key: string) => void
  onSelect: (id: string, range: boolean) => void
}) {
  const { t } = useTranslation()
  const td = 'px-3 py-2 text-meta text-ink-body'
  const row = (c: BoardCard) => {
    const sla = slaCell(c.sla, c.status, now)
    const on = selected.has(c.id)
    return (
      <tr key={c.id} data-flip-id={c.id} className={cn('border-b border-line last:border-b-0 hover:bg-surface-subtle', on && 'bg-accent')}>
        <td className="w-10 px-3 py-2">
          <button
            type="button"
            role="checkbox"
            aria-checked={on}
            aria-label={t('boards.bulk.select', { key: c.key })}
            onClick={(e) => onSelect(c.id, e.shiftKey)}
            className={cn(
              'flex size-4 items-center justify-center rounded border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              on ? 'border-primary bg-primary text-primary-foreground' : 'border-line-strong',
            )}
          >
            {on ? <Check className="size-3" aria-hidden /> : null}
          </button>
        </td>
        <td className={cn(td, 'w-28 font-mono text-label text-ink-muted')}>
          <span className="flex items-center gap-2">
            <span className={cn('size-2 shrink-0 rounded-sm', priorityDotClass(c.priority))} aria-label={c.priority} />
            {c.key}
          </span>
        </td>
        <td className={td}>
          <button type="button" onClick={() => onOpen(c.key)} className="w-full truncate text-left font-medium text-ink-strong hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {c.subject}
          </button>
        </td>
        <td className={cn(td, 'w-44 truncate')}>{c.customer?.name}</td>
        <td className={cn(td, 'w-40')}>
          <span className={cn('inline-block truncate rounded-sm px-2 py-[2px] text-label font-medium', statusPillClass(c.status))}>{statusLabel(c.status)}</span>
        </td>
        <td className={cn(td, 'w-36')}>
          <AssigneePicker
            ticketKey={c.key}
            assignee={c.assignee}
            agents={agents}
            meId={meId}
            canAssign={canAssign}
            size="sm"
            showName
            onChange={(value) => onAction(c, { kind: 'assignee', value })}
          />
        </td>
        <td className={cn(td, 'w-24 text-right font-mono text-label', SLA_TONE_CLASS[sla.tone])}>{sla.text}</td>
      </tr>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {columns
        .filter((c) => c.cards.length > 0)
        .map((col) => (
          <section key={col.id} aria-label={label(col)} className="overflow-hidden rounded-lg border border-line bg-background">
            <h2 className="flex items-center gap-2 border-b border-line bg-surface-page px-3 py-2 text-meta font-semibold text-ink">
              {label(col)}
              <span className="rounded-full bg-surface-subtle px-2 font-mono text-label font-normal text-ink-muted">{col.cards.length}</span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] table-fixed border-collapse">
                {/* Fixed layout takes widths from the first row; the header row is screen-reader only, so set them here. */}
                <colgroup>
                  <col className="w-10" />
                  <col className="w-28" />
                  <col />
                  <col className="w-44" />
                  <col className="w-44" />
                  <col className="w-36" />
                  <col className="w-24" />
                </colgroup>
                <thead className="sr-only">
                  <tr>
                    <th className={thClass} />
                    <th className={thClass}>{t('queue.columns.id', { defaultValue: 'ID' })}</th>
                    <th className={thClass}>{t('boards.list.subject')}</th>
                    <th className={thClass}>{t('boards.list.customer')}</th>
                    <th className={thClass}>{t('boards.list.status')}</th>
                    <th className={thClass}>{t('boards.list.assignee')}</th>
                    <th className={thClass}>SLA</th>
                  </tr>
                </thead>
                <tbody>{col.cards.map(row)}</tbody>
              </table>
            </div>
          </section>
        ))}
    </div>
  )
}
